// Console Urgence : les règles PURES du tableau de travail d'une urgence — l'étape en cours, le prochain contact, ce que devient la liste d'étapes quand on
// note un appel ou qu'on passe à la suite, la phrase du journal, le retard du délai de décision. Testées dans regles.test.ts (DECISIONS §29).
import type { CascadeEtape, ContactAppele, ContactUrgence, ContexteUrgence, GroupeContacts, ResultatAppel } from '../../types';

export const LIBELLE_COURT: Record<string, string> = {
  sur_place: 'Relais sur place', interne_pole: 'Internes du même pôle', externe_pole: 'Externes du même pôle',
  interne_hors_pole: 'Internes hors pôle', admin: 'Admin sur site', escalade: 'Escalade management',
};
export const LIBELLE_RESULTAT: Record<ResultatAppel, string> = { pas_de_reponse: 'pas de réponse', refuse: 'refuse', accepte: 'accepte' };

const GROUPES: GroupeContacts[] = ['interne_pole', 'externe_pole', 'interne_hors_pole'];
export const estGroupeDeContacts = (cle: string): cle is GroupeContacts => (GROUPES as string[]).includes(cle);

/** L'étape en cours ; sinon la première à faire ; -1 quand tout est fait. */
export function etapeCourante(etapes: CascadeEtape[]): number {
  const enCours = etapes.findIndex((e) => e.statut === 'en_cours');
  return enCours !== -1 ? enCours : etapes.findIndex((e) => e.statut === 'a_faire');
}

export function appelDe(etape: CascadeEtape | undefined, personneId: number): ContactAppele | undefined {
  return etape?.contacts?.find((c) => c.personne_id === personneId);
}

/** Les contacts qu'on peut appeler : disponibles seulement (les autres restent visibles, repliés, mais ne sont pas proposés). */
export const disponibles = (l: ContactUrgence[] | undefined): ContactUrgence[] => (l ?? []).filter((c) => c.dispo.k === 'ok');

export interface ProchainContact { index: number; contact: ContactUrgence | null; epuise: boolean }

/** Le prochain contact de l'étape en cours : le premier disponible qu'on n'a pas encore appelé. `epuise` = tous les disponibles ont été appelés (ou il n'y en a aucun). */
export function prochainContact(etapes: CascadeEtape[], contacts: ContexteUrgence['contacts'] | null): ProchainContact | null {
  const index = etapeCourante(etapes);
  if (index === -1) return null;
  const etape = etapes[index];
  if (!estGroupeDeContacts(etape.cle) || !contacts) return { index, contact: null, epuise: false };
  const dispo = disponibles(contacts[etape.cle]);
  const suivant = dispo.find((c) => !appelDe(etape, c.personne_id)) ?? null;
  return { index, contact: suivant, epuise: suivant == null };
}

/** Note (ou remplace) l'appel d'un contact sur une étape. L'étape devient « en cours » si elle ne l'était pas encore. */
export function noterAppel(etapes: CascadeEtape[], cle: string, personneId: number, resultat: ResultatAppel, parNom: string, maintenant: string): CascadeEtape[] {
  return etapes.map((e) => {
    if (e.cle !== cle) return e;
    const autres = (e.contacts ?? []).filter((c) => c.personne_id !== personneId);
    return { ...e, statut: e.statut === 'a_faire' ? 'en_cours' : e.statut, horodatage: e.horodatage ?? maintenant, contacts: [...autres, { personne_id: personneId, resultat, horodatage: maintenant, par_nom: parNom }] };
  });
}

/** Retire l'appel noté d'un contact (« Corriger »). */
export function corrigerAppel(etapes: CascadeEtape[], cle: string, personneId: number): CascadeEtape[] {
  return etapes.map((e) => (e.cle === cle ? { ...e, contacts: (e.contacts ?? []).filter((c) => c.personne_id !== personneId) } : e));
}

/**
 * « Passer à la suivante » : l'étape en cours est notée en échec (ou sautée), la suivante démarre (comme le bouton « Échec » d'avant, qui mettait la suivante
 * en cours). `parNom` = qui l'a fait, affiché dans « Où en est la cascade ».
 */
export function passerALaSuivante(etapes: CascadeEtape[], maintenant: string, parNom: string, statut: 'echec' | 'saute' = 'echec'): CascadeEtape[] {
  const i = etapeCourante(etapes);
  if (i === -1) return etapes;
  return etapes.map((e, j) => (j === i ? { ...e, statut, horodatage: maintenant, par_nom: parNom }
    : j === i + 1 && e.statut === 'a_faire' ? { ...e, statut: 'en_cours' as const, horodatage: maintenant, par_nom: parNom } : e));
}

/** Les appels notés « accepte » sur une urgence encore en cours : quelqu'un a dit oui, le remplacement attend sa confirmation. Le dernier compte. */
export function accepteEnAttente(etapes: CascadeEtape[]): { cle: string; personneId: number } | null {
  let trouve: { cle: string; personneId: number; at: string } | null = null;
  for (const e of etapes) for (const c of e.contacts ?? []) if (c.resultat === 'accepte' && (!trouve || c.horodatage >= trouve.at)) trouve = { cle: e.cle, personneId: c.personne_id, at: c.horodatage };
  return trouve ? { cle: trouve.cle, personneId: trouve.personneId } : null;
}

/** « David Viale : pas de réponse » — la ligne du journal d'un appel. */
export const resumeAppel = (nom: string, resultat: ResultatAppel): string => `${nom} : ${LIBELLE_RESULTAT[resultat]}`;

/** « Étape 2/6 · Internes du même pôle » */
export function libelleEtapeCourante(etapes: CascadeEtape[]): string {
  const i = etapeCourante(etapes);
  return i === -1 ? 'Toutes les étapes sont faites' : `Étape ${i + 1}/${etapes.length} · ${LIBELLE_COURT[etapes[i].cle] ?? etapes[i].libelle}`;
}

/** L'escalade est en cours depuis plus que le délai de décision du management. */
export function minutesEscalade(etapes: CascadeEtape[] | null | undefined, maintenant: number): number | null {
  const e = (etapes ?? []).find((x) => x.cle === 'escalade');
  return e?.statut === 'en_cours' && e.horodatage ? Math.round((maintenant - new Date(e.horodatage).getTime()) / 60000) : null;
}
export function retardEscalade(etapes: CascadeEtape[] | null | undefined, maintenant: number, seuilMinutes: number): number {
  const m = minutesEscalade(etapes, maintenant);
  return m != null && m > seuilMinutes ? m - seuilMinutes : 0;
}

/** « 25 min », « 1 h 05 » */
export function duree(minutes: number): string {
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60), m = minutes % 60;
  return `${h} h${m ? ` ${String(m).padStart(2, '0')}` : ''}`;
}
/** « il y a 25 min » / « à l'instant » */
export const ilYa = (minutes: number): string => (minutes < 1 ? "à l'instant" : `il y a ${duree(minutes)}`);

/** « Alexia S. » : la place d'un titre est comptée. */
export function prenomNom(n: string): string { return n.trim().split(/\s+/)[0] ?? n; }
